// The shift-end notes review feed and approval (architecture sprint-15 §9.1, §9.2). The feed is polled every
// 30 s while the viewer may review (me.access.notesReview); in background tabs too when they get browser
// notifications (notesReviewNotify). Silent: the badges keep their last value on error.
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { LIVE_REFETCH_MS } from '@shared/report-rules';
import { toast } from 'sonner';
import { notesKey } from '@/hooks/use-notes';
import { useMe } from '@/hooks/use-me';
import { apiRequest, ApiClientError } from '@/lib/api';
import type { Note, NotesReviewDto } from '@/types/notes';

export const NOTES_REVIEW_KEY = ['oversight', 'notes-review'] as const;
const OVERSIGHT_PROJECTS_KEY = ['oversight', 'projects'] as const;
const APPROVE_ERROR = "Couldn't approve. The list has been refreshed.";

export function useNotesReview(): UseQueryResult<NotesReviewDto> {
  const { data: me } = useMe();
  return useQuery({
    queryKey: NOTES_REVIEW_KEY,
    queryFn: () => apiRequest<NotesReviewDto>('/api/oversight/notes-review'),
    enabled: me?.access.notesReview === true,
    meta: { silent: true },
    refetchInterval: LIVE_REFETCH_MS,
    refetchIntervalInBackground: me?.access.notesReviewNotify === true,
    refetchOnWindowFocus: true,
  });
}

/** Badge counts: the total and per project (0 when the feed is off or not loaded). */
export function useNotesReviewCounts(): { total: number; byProject: Map<string, number> } {
  const { data } = useNotesReview();
  return { total: data?.total ?? 0, byProject: new Map((data?.projects ?? []).map((p) => [p.projectId, p.count])) };
}

/** POST …/notes/approve. A 409/422 (or any error) → a toast and a refresh, so the list shows the truth. */
export function useApproveNotes(projectId: string): UseMutationResult<{ approvedCount: number; notes: Note[] }, Error, string[]> {
  const queryClient = useQueryClient();
  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: notesKey(projectId) }),
      queryClient.invalidateQueries({ queryKey: NOTES_REVIEW_KEY }),
      queryClient.invalidateQueries({ queryKey: OVERSIGHT_PROJECTS_KEY }),
    ]);
  };
  return useMutation({
    mutationFn: (noteIds) => apiRequest(`/api/oversight/projects/${projectId}/notes/approve`, { method: 'POST', body: JSON.stringify({ noteIds }) }),
    meta: { silent: true },
    onSuccess: refresh,
    onError: async (err) => {
      toast.error(err instanceof ApiClientError ? err.message : APPROVE_ERROR);
      await refresh();
    },
  });
}
